import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Checkbox, RadioGroup } from 'component-library'
import copy from 'copy-to-clipboard'
import { Tooltip } from 'react-tooltip'
import { QCHEM_RADIO_BUTTONS } from './helpers'
import { DatasetContext } from '../../context'
import { sendCustomGAEvent } from 'shared-utilities'
import './DownloadForm.scss'

interface IDownloadFormProps {
  category: string
  isVisible: boolean
}

const DownloadForm = ({ category, isVisible }: IDownloadFormProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const {
    attributeNames,
    selectedAttributes,
    setSelectedAttributes,
    selectedBondlength,
    selectedBasis,
    selectedPeriodicity,
    selectedLattice,
    selectedLayout,
    selectedConfiguration,
    setSelectedConfiguration,
    downloadName,
  } = useContext(DatasetContext)

  /* TODO: Once pennylane is using the datasets API, remove this
  code and use the real class name and parameters for 'other' datasets */
  const DOWNLOAD_STRING_BASE = useMemo(() => {
    if (category === 'qchem') {
      return `"qchem", molname="${downloadName}"`
    } else if (category === 'qspin') {
      return `"qspin", sysname="${downloadName}"`
    }
    return `"other", name="${downloadName}"`
  }, [])

  const [showCopyConfirmation, setShowCopyConfirmation] = useState(false)

  const [attributesString, updateAttributesString] = useState<string>('')

  // qchem download options
  const [bondlengthString, updateBondlengthString] = useState<string>('')
  const [basisString, updateBasisString] = useState<string>('')

  // qspin download options
  const [periodicityString, updatePeriodicityString] = useState<string>('')
  const [latticeString, updateLatticeString] = useState<string>('')
  const [layoutString, updateLayoutString] = useState<string>('')

  const downloadCode = useMemo(() => {
    return `qml.data.load(${DOWNLOAD_STRING_BASE}${bondlengthString}${basisString}${periodicityString}${latticeString}${layoutString}${attributesString})`.replaceAll(
      'all',
      'full'
    )
  }, [
    bondlengthString,
    basisString,
    periodicityString,
    latticeString,
    layoutString,
    attributesString,
  ])

  useEffect(() => {
    if (isVisible && ref.current) {
      const bounding = ref.current.getBoundingClientRect()
      if (
        bounding.bottom >
        (window.innerHeight || document.documentElement.clientHeight)
      ) {
        ref.current.style.top = `${
          ((window.innerHeight || document.documentElement.clientHeight) -
            bounding.bottom) /
          4
        }px`
      }
    }
  }, [isVisible])

  useEffect(() => {
    if (selectedAttributes.length < attributeNames.length) {
      updateAttributesString(
        `, attributes=[${selectedAttributes
          .map((attribute) => `"${attribute}"`)
          .join(', ')}]`
      )
    } else if (selectedAttributes.length === attributeNames.length) {
      updateAttributesString('')
    }

    if (category === 'qchem') {
      updateBondlengthString(
        selectedConfiguration === 'bondlength'
          ? `, bondlength=${selectedBondlength}`
          : ', bondlength="full"'
      )
      updateBasisString(selectedBasis ? `, basis="${selectedBasis}"` : '')
    }

    if (category === 'qspin') {
      updatePeriodicityString(
        selectedPeriodicity
          ? `, periodicity="${selectedPeriodicity.toLowerCase()}"`
          : ''
      )
      updateLatticeString(
        selectedLattice ? `, lattice="${selectedLattice}"` : ''
      )
      updateLayoutString(selectedLayout ? `, layout="${selectedLayout}"` : '')
    }
  }, [
    selectedAttributes,
    selectedBondlength,
    selectedConfiguration,
    selectedPeriodicity,
    selectedLattice,
    selectedLayout,
    selectedBasis,
  ])

  const handleCheck = (attribute: string): void => {
    const updatedSelectedAttributes = [...selectedAttributes]
    if (updatedSelectedAttributes.includes(attribute)) {
      updatedSelectedAttributes.splice(
        updatedSelectedAttributes.indexOf(attribute),
        1
      )
    } else {
      updatedSelectedAttributes.push(attribute)
    }
    setSelectedAttributes(updatedSelectedAttributes)
  }

  const { user } = useAuth0()

  const copyCodeToClipboard = () => {
    copy(downloadCode)
    setShowCopyConfirmation(true)
    sendCustomGAEvent('copy-download-code', {
      event_category: 'dataset',
      event_label: downloadName,
      value: JSON.stringify({
        id: user?.sub || '',
      }),
    })
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCopyConfirmation(false)
    }, 2000)
    return () => clearTimeout(timer)
  }, [showCopyConfirmation])

  return isVisible ? (
    <div className="DownloadForm" ref={ref}>
      {category === 'qchem' && (
        <div
          className="DownloadForm__radio-buttons"
          data-testid="configuration-radio-buttons"
        >
          <RadioGroup
            className="mb-0"
            radioButtons={QCHEM_RADIO_BUTTONS}
            selectedValue={selectedConfiguration}
            onChange={
              setSelectedConfiguration as Dispatch<SetStateAction<string>>
            }
          />
        </div>
      )}
      <div className="DownloadForm__checkboxes">
        {!selectedAttributes.length && (
          <div className="text-error-2 mb-2 text-xs">
            Please select at least one attribute
          </div>
        )}
        <Checkbox
          testId="all-attributes-checkbox"
          isChecked={selectedAttributes.length === attributeNames.length}
          fieldName="all-attributes"
          label="All Attributes"
          onChange={() =>
            selectedAttributes.length === attributeNames.length
              ? setSelectedAttributes([])
              : setSelectedAttributes(attributeNames)
          }
          className="mb-4"
        />
        <div className="flex flex-row flex-wrap">
          {attributeNames.map((attribute) => (
            <div
              key={`${attribute}-checkbox`}
              className="mb-2 basis-1/2 flex pl-6"
            >
              <Checkbox
                testId="attribute-checkbox"
                isChecked={selectedAttributes.includes(attribute)}
                fieldName={attribute}
                label={attribute}
                onChange={() => handleCheck(attribute)}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="DownloadForm__download-wrapper">
        <p>Access from PennyLane:</p>
        <Tooltip
          id="copy-code-button"
          data-testid="copy-code-tooltip"
          content="Copied to clipboard"
          isOpen={showCopyConfirmation}
          className="Copy__tooltip"
        />
        <button
          className={`DownloadForm__button ${
            selectedAttributes.length && downloadCode.length > 350
              ? 'h-[140px]'
              : ''
          }`}
          data-tooltip-id="copy-code-button"
          aria-label="copy code to clipboard"
          onClick={copyCodeToClipboard}
          data-testid="download-code"
          disabled={!selectedAttributes.length}
        >
          <i className="bx bx-copy-alt" />
          <code>
            {!selectedAttributes.length
              ? 'Please select at least one attribute'
              : downloadCode}
          </code>
        </button>
      </div>
      <div className="DownloadForm__info-wrapper">
        <div>
          <i className="bx bx-info-circle" />
        </div>
        <div>
          <p>
            You can copy and paste this code into your Python script or Jupyter
            notebook to download the datasets via PennyLane
          </p>
        </div>
      </div>
    </div>
  ) : null
}

export default DownloadForm
